import classNames from 'classnames';
import { GatsbyImage } from "gatsby-plugin-image";
import React from 'react';

import AnimateReveal from '../AnimateReveal/AnimateReveal';
import Button, { themes, sizes } from '../UI/Button/Button';
import HtmlContent from '../UI/HtmlContent/HtmlContent';
import Teaser from '../UI/Teaser/Teaser';
import Type from '../UI/Type/Type';
import { motion } from '../UI/framer-motion-custom';
import * as styles from './SplitBlock.module.scss';

const transition = {
  duration: 0.8,
  ease: 'circOut',
};

const imgVariants = {
  hidden: {
    opacity: 0,
    y: '320px',
    transition,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition,
  },
};

const SplitBlock = ({
  teaser,
  heading,
  body,
  link,
  image,
  imagePosition = 'auto',
  layout = 'imageSide',
  children,
  buttonLink,
  buttonLabel,
  buttonStyle = themes.LINK,
  theme,
  hasImageOffset = true,
  hasSpacer = true,
  spacerSize = 'default',
  ...other
}) => {
  const isIntro = layout === 'intro';
  const isImageTop = layout === 'imageTop';
  const isImageSide = layout === 'imageSide';

  const classname = classNames(styles.container, {
    [ styles.layoutImageSide ]: isImageSide,
    [ styles.layoutImageTop ]: isImageTop,
    [ styles.layoutIntro ]: isIntro,
    [ styles.hasImageOffset ]: hasImageOffset,
    [ styles.hasSpacer ]: hasSpacer && spacerSize === 'default',
    [ styles.hasSpacerLg ]: hasSpacer && spacerSize === 'lg',
    [ styles.hasCta ]: !!buttonLink,
    [ styles[ theme ] ]: !!theme,
  }, styles[ imagePosition ] );

  const isBtnWhite = theme === 'plum' || theme === 'navy'; 

  return (
    <section className={classname} {...other}>
      <div className={styles.inner}>
        {image && !isIntro && (
          <AnimateReveal
            at={`140px`}
            as={motion.div}
            className={styles.imageContainer}
            variants={imgVariants}
          >
            <GatsbyImage
              image={image?.localFile?.childImageSharp?.gatsbyImageData}
              className={styles.image} />
          </AnimateReveal>
        )}

        {!isImageSide && teaser && <Teaser>{teaser}</Teaser>}

        <AnimateReveal className={styles.content} staggerChildren={true}>
          {isImageSide && teaser && <Teaser>{teaser}</Teaser>}

          {heading &&
            <Type.H2 className={styles.heading} as={motion.h2}>
              {heading}
            </Type.H2>
          }

          <motion.div className={styles.body}>
            {children ? (
              children
            ) : (
              <>
                <HtmlContent content={body} />

                {buttonLink && (
                  <Button
                    theme={buttonStyle}
                    size={sizes.LARGE}
                    to={buttonLink.url}
                    isReverse={isBtnWhite}
                  >
                    {buttonLabel || `Learn more`}
                  </Button>
                )}
              </>
            )}
          </motion.div>
        </AnimateReveal>
      </div>
    </section>
  );
};

export default SplitBlock;
